import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const SummaryItem = ({ label, value, subValue, valueColor = 'text-white', className = '' }) => (
  <div className={`flex flex-col items-center justify-center p-3 bg-slate-900 rounded-lg text-center h-full text-white ${className}`}>
    <span className="text-xs text-gray-400 whitespace-nowrap">{label}</span>
    <span className={`text-xl font-bold mt-1 ${valueColor}`}>{value}</span>
    {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
  </div>
);

const getTradeLabel = (trade, symbolA, symbolB, fallback) => {
  if (!trade) return fallback;

  const primarySide = trade.pos_a || trade.entries?.[0]?.pos_a;
  const secondarySide = trade.pos_b || trade.entries?.[0]?.pos_b;
  const primaryLabel = symbolA && primarySide ? `${symbolA} ${primarySide}` : symbolA || primarySide || '';
  const secondaryLabel = symbolB && secondarySide ? `${symbolB} ${secondarySide}` : symbolB || secondarySide || '';

  return [primaryLabel, secondaryLabel].filter(Boolean).join(' / ') || fallback;
};

const StrategySummary = ({ summary, completedTrades = [], strategyParams }) => {
  const { t, i18n } = useTranslation();
  const formattedAvgDuration = useMemo(() => {
    if (!summary?.avg_duration) return 'N/A';
    return summary.avg_duration.replace(/ \d+ seconds?/, '');
  }, [summary]);
  const copy = useMemo(() => {
    if (i18n.language === 'ru') {
      return {
        bestTrade: 'Макс. прибыльная позиция',
        worstTrade: 'Макс. убыточная позиция',
        noTrade: 'Нет сделок'
      };
    }

    return {
      bestTrade: 'Best Position',
      worstTrade: 'Worst Position',
      noTrade: 'No trades'
    };
  }, [i18n.language]);
  const extremes = useMemo(() => {
    if (!Array.isArray(completedTrades) || completedTrades.length === 0) {
      return { bestTrade: null, worstTrade: null };
    }

    return completedTrades.reduce((acc, trade) => {
      const pnl = Number(trade?.net_pnl_usdt);
      if (!Number.isFinite(pnl)) return acc;

      if (!acc.bestTrade || pnl > Number(acc.bestTrade.net_pnl_usdt)) {
        acc.bestTrade = trade;
      }

      if (!acc.worstTrade || pnl < Number(acc.worstTrade.net_pnl_usdt)) {
        acc.worstTrade = trade;
      }

      return acc;
    }, { bestTrade: null, worstTrade: null });
  }, [completedTrades]);
  const symbolA = strategyParams?.symbol_a;
  const symbolB = strategyParams?.symbol_b;

  if (!summary) {
    return (
      <div className="self-start bg-slate-800 p-6 rounded-xl shadow-lg">
        <h3 className="text-xl font-semibold text-white">{t('summary.title')}</h3>
        <p className="text-center text-gray-400 py-4">{t('summary.loading')}</p>
      </div>
    );
  }

  const pnlColorClass = summary.net_total_pnl >= 0 ? 'text-green-400' : 'text-red-400';

  return (
    <div className="self-start bg-slate-800 p-6 rounded-xl shadow-lg">
      <h3 className="text-xl font-semibold text-white mb-4">{t('summary.title')}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="flex flex-col items-center justify-center p-3 rounded-lg bg-slate-900 md:col-span-2">
          <span className="text-xs text-gray-400">{t('summary.net_pnl')}</span>
          <span className={`text-2xl font-bold mt-1 ${pnlColorClass}`}>
            ${(summary.net_total_pnl || 0).toFixed(2)}
          </span>
        </div>
        <SummaryItem label={t('summary.total_trades')} value={summary.total || 0} subValue={`W: ${summary.wins} / L: ${summary.losses}`} />
        <SummaryItem label={t('summary.winrate')} value={`${(summary.winrate || 0).toFixed(2)}%`} />
        <SummaryItem label={t('summary.profit_factor')} value={summary.profit_factor === Infinity ? '∞' : (summary.profit_factor || 0).toFixed(2)} />
        <SummaryItem label={t('summary.avg_pnl')} value={`$${(summary.avg_pnl || 0).toFixed(2)}`} />
        <SummaryItem label={t('summary.commission')} value={`$${(summary.total_commission || 0).toFixed(2)}`} />
        <SummaryItem label={t('summary.duration')} value={formattedAvgDuration} />
        <SummaryItem
          label={copy.bestTrade}
          value={extremes.bestTrade ? `$${(Number(extremes.bestTrade.net_pnl_usdt) || 0).toFixed(2)}` : '—'}
          subValue={getTradeLabel(extremes.bestTrade, symbolA, symbolB, copy.noTrade)}
          valueColor="text-emerald-400"
          className="md:col-span-2"
        />
        <SummaryItem
          label={copy.worstTrade}
          value={extremes.worstTrade ? `$${(Number(extremes.worstTrade.net_pnl_usdt) || 0).toFixed(2)}` : '—'}
          subValue={getTradeLabel(extremes.worstTrade, symbolA, symbolB, copy.noTrade)}
          valueColor="text-rose-400"
          className="md:col-span-2"
        />
      </div>
    </div>
  );
};

export default StrategySummary;
